import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarDateInput from "../../../common/dashboard/CalendarDateInput";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CmpInvoiceDisplayStateEnum, CmpInvoiceKindEnum } from "../../../../graphql/types";
import { isOutstandingInvoiceDisplayState } from "./manageInvoiceActions";
import { CurrencyInput } from "../../../common/dashboard/CurrencyInput";
import {
  centsToCurrencyValue,
  currencyValueToCents,
} from "../../../common/dashboard/currencyUnits";
import {
  chamberDateInputToUtcMilli,
  getChamberLocalDateInputValue,
} from "../invoiceDueDate";
import {
  InvoiceDialogCloseButton,
  invoiceDesignFontFamily,
  invoiceDesignTokens,
  invoiceDialogPaperSx,
  invoiceFieldSx,
  invoiceModalActionSx,
} from "../InvoiceDesignSystem";
import { formatCurrency } from "../invoiceFormatting";
import {
  enterFieldMessage,
  selectFieldMessage,
} from "../../../../utils/fieldMessages";
import InvoiceSearchableSelect from "../InvoiceSearchableSelect";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../../utils/userFacingError";

export type InvoiceLifecycleActionKind =
  "DELETE_DRAFT" | "VOID" | "ISSUE_CREDIT";

export type InvoiceLifecycleReason =
  | "CUSTOMER_CANCELLED"
  | "EVENT_REGISTRATION_CANCELLED"
  | "MEMBERSHIP_CANCELLED"
  | "SPONSORSHIP_ADVERTISING_CANCELLED"
  | "BILLING_ERROR"
  | "DUPLICATE_PAYMENT"
  | "OTHER";

export type InvoiceLifecycleSourceEffect = "PRESERVE_SOURCE" | "REVERSE_SOURCE";

export type InvoiceLifecycleActionSubmit = {
  action: InvoiceLifecycleActionKind;
  amountInCents?: number;
  reason?: InvoiceLifecycleReason;
  reasonExplanation?: string;
  refundedAtUtcMilli?: number;
  refundMethodLabel?: string;
  refundReference?: string;
  sourceEffect?: InvoiceLifecycleSourceEffect;
};

export type InvoiceLifecycleActionDialogProps = {
  action: InvoiceLifecycleActionKind;
  actionError?: string | null;
  allowPartialCredit?: boolean;
  creditableAmountInCents?: number;
  customerName: string;
  hasLinkedSource?: boolean;
  invoiceNumber: string;
  invoiceKind?: CmpInvoiceKindEnum;
  invoiceDisplayState?: CmpInvoiceDisplayStateEnum;
  // A membership application or renewal invoice: the backend refuses any
  // reason that would cancel the membership from Manage Invoices.
  isMembershipInvoice?: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (input: InvoiceLifecycleActionSubmit) => Promise<void> | void;
  open: boolean;
  refundMethodOptions?: string[];
  timeZone?: string | null;
};

type ReasonOption = {
  label: string;
  value: InvoiceLifecycleReason;
};

type SourceEffectOption = {
  label: string;
  value: InvoiceLifecycleSourceEffect;
};

const sourceEffectOptions: SourceEffectOption[] = [
  {
    label:
      "Reverse the linked registration, membership, sponsorship, or advertising record",
    value: "REVERSE_SOURCE",
  },
  { label: "Keep the linked source record unchanged", value: "PRESERVE_SOURCE" },
];

type InvoiceLifecycleRequiredField = "reason" | "explanation" | "sourceEffect";

// Design D:637714-640900: each empty required field shows its own message.
const requiredFieldMessage = "This field is required";

const lifecycleFieldLabelClassName =
  "mb-1.5 block text-sm font-semibold text-[#0A0A0A]";

const sharedReasonOptions: ReasonOption[] = [
  { label: "Customer Cancelled", value: "CUSTOMER_CANCELLED" },
  {
    label: "Event Registration Cancelled",
    value: "EVENT_REGISTRATION_CANCELLED",
  },
  { label: "Membership Cancelled", value: "MEMBERSHIP_CANCELLED" },
  {
    label: "Sponsorship/Advertising Cancelled",
    value: "SPONSORSHIP_ADVERTISING_CANCELLED",
  },
  { label: "Billing Error", value: "BILLING_ERROR" },
];

export const voidInvoiceReasonOptions: ReasonOption[] = [
  ...sharedReasonOptions,
  { label: "Duplicate Payment", value: "DUPLICATE_PAYMENT" },
  { label: "Other", value: "OTHER" },
];

export const creditInvoiceReasonOptions: ReasonOption[] = [
  ...sharedReasonOptions,
  { label: "Duplicate Payment", value: "DUPLICATE_PAYMENT" },
  { label: "Other", value: "OTHER" },
];

const cancellationReasons = new Set<InvoiceLifecycleReason>([
  "CUSTOMER_CANCELLED",
  "EVENT_REGISTRATION_CANCELLED",
  "MEMBERSHIP_CANCELLED",
  "SPONSORSHIP_ADVERTISING_CANCELLED",
]);

export const getInvoiceLifecycleSourceEffect = ({
  action,
  invoiceKind,
  invoiceDisplayState,
  hasLinkedSource,
  reason,
  selectedSourceEffect,
}: {
  action?: InvoiceLifecycleActionKind;
  invoiceKind?: CmpInvoiceKindEnum;
  invoiceDisplayState?: CmpInvoiceDisplayStateEnum;
  hasLinkedSource: boolean;
  reason: InvoiceLifecycleReason | "";
  selectedSourceEffect: InvoiceLifecycleSourceEffect | "";
}): InvoiceLifecycleSourceEffect | undefined => {
  if (invoiceKind === CmpInvoiceKindEnum.EventRegistration && reason &&
    (action === "VOID" || (action === "ISSUE_CREDIT" && invoiceDisplayState && isOutstandingInvoiceDisplayState(invoiceDisplayState)))) {
    return "REVERSE_SOURCE";
  }
  // A sponsorship void always releases the sponsor, its tickets and its level.
  if (invoiceKind === CmpInvoiceKindEnum.EventSponsorship && reason && action === "VOID") {
    return "REVERSE_SOURCE";
  }
  if (!hasLinkedSource || !reason) {
    return undefined;
  }

  if (cancellationReasons.has(reason)) {
    return "REVERSE_SOURCE";
  }

  if (reason === "BILLING_ERROR" || reason === "DUPLICATE_PAYMENT") {
    return "PRESERVE_SOURCE";
  }

  return selectedSourceEffect || undefined;
};

export const canIssuePartialInvoiceCredit = ({
  allowPartialCredit,
  sourceEffect,
}: {
  allowPartialCredit: boolean;
  sourceEffect?: InvoiceLifecycleSourceEffect;
}) => allowPartialCredit && sourceEffect !== "REVERSE_SOURCE";

// Mirror of the backend rule (cap-backend cmpInvoices voidCmpInvoiceForChamber
// and issueCmpInvoiceCreditForChamber): a membership invoice voided or
// credited from Manage Invoices is refused when the source effect is
// REVERSE_SOURCE, which every cancellation reason and Other with "Reverse the
// linked ..." resolve to. These choices are disabled instead of failing.
export const isInvoiceLifecycleChoiceRefusedForMembership = ({
  isMembershipInvoice,
  reason,
  sourceEffect,
}: {
  isMembershipInvoice: boolean;
  reason: InvoiceLifecycleReason | "";
  sourceEffect?: InvoiceLifecycleSourceEffect | "";
}) =>
  isMembershipInvoice &&
  (cancellationReasons.has(reason as InvoiceLifecycleReason) ||
    (reason === "OTHER" && sourceEffect === "REVERSE_SOURCE"));

// The same text as cap-backend getCmpMembershipInvoiceCancellationGuidance
// (cmpMemberUserMessages.ts), which the refusal message also uses. Keep the
// two identical; both tests pin the literal text.
export const getMembershipInvoiceCancellationGuidance = (
  action: Exclude<InvoiceLifecycleActionKind, "DELETE_DRAFT">,
) => {
  const verb = action === "VOID" ? "void" : "credit";
  const reasonField = action === "VOID" ? "Reason for Voiding" : "Reason for Credit";
  return `A membership can only be cancelled from the member's page: go to Members > Manage Members, click Update on this member, then click Delete Member. To ${verb} only this invoice and keep the membership, choose Billing Error as the ${reasonField}, or choose Other and set Linked Source Action to "Keep the linked source record unchanged".`;
};

const actionContent = {
  DELETE_DRAFT: {
    confirmLabel: "Delete Invoice",
    description:
      "This draft invoice will be permanently deleted and cannot be restored. The deletion will be recorded in the Activity Log.",
    title: "Delete Draft Invoice?",
  },
  ISSUE_CREDIT: {
    confirmLabel: "Issue Credit",
    description:
      "Create a credit invoice linked to the original paid invoice. The original invoice and payment history will remain unchanged.",
    title: "Issue Credit",
  },
  VOID: {
    confirmLabel: "Void Invoice",
    description:
      "Void this issued unpaid invoice, set its outstanding balance to $0.00, and retain it in the transaction history.",
    title: "Void Invoice",
  },
} as const;

const amountInputClassName =
  "h-[42px] w-full rounded-lg border border-[#9E9E9E] bg-white pl-7 pr-3 text-sm text-[#0A0A0A] outline-none transition placeholder:text-[#8A8A8A] focus:border-[#E1A731]";

const InvoiceLifecycleActionDialog = ({
  action,
  actionError,
  allowPartialCredit = true,
  creditableAmountInCents = 0,
  customerName,
  hasLinkedSource = false,
  invoiceNumber,
  invoiceKind,
  invoiceDisplayState,
  isMembershipInvoice = false,
  loading = false,
  onClose,
  onConfirm,
  open,
  refundMethodOptions = [],
  timeZone,
}: InvoiceLifecycleActionDialogProps) => {
  const noun = useCmpOrganizationNoun();
  const [reason, setReason] = useState<InvoiceLifecycleReason | "">("");
  const [reasonExplanation, setReasonExplanation] = useState("");
  const [sourceEffect, setSourceEffect] = useState<
    InvoiceLifecycleSourceEffect | ""
  >("");
  const [creditMode, setCreditMode] = useState<"FULL" | "PARTIAL">("FULL");
  const [creditAmount, setCreditAmount] = useState(
    centsToCurrencyValue(creditableAmountInCents),
  );
  const [refundMethodLabel, setRefundMethodLabel] = useState("");
  const [refundDate, setRefundDate] = useState(() =>
    getChamberLocalDateInputValue({ timeZone }),
  );
  const [refundReference, setRefundReference] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<InvoiceLifecycleRequiredField, true>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitInFlightRef = useRef(false);
  const effectiveLoading = loading || submitting;
  const copy = actionContent[action];
  const creditsAndVoidsEventRegistration = action === "ISSUE_CREDIT" &&
    invoiceKind === CmpInvoiceKindEnum.EventRegistration && !!invoiceDisplayState &&
    isOutstandingInvoiceDisplayState(invoiceDisplayState);
  const cancelsEventRegistration = creditsAndVoidsEventRegistration ||
    (action === "VOID" && invoiceKind === CmpInvoiceKindEnum.EventRegistration);
  // Event voids always release their source, so there is no source choice.
  const releasesEventSource = cancelsEventRegistration ||
    (action === "VOID" && invoiceKind === CmpInvoiceKindEnum.EventSponsorship);
  const reasonOptions =
    action === "ISSUE_CREDIT"
      ? creditInvoiceReasonOptions
      : voidInvoiceReasonOptions;
  const resolvedSourceEffect = getInvoiceLifecycleSourceEffect({
    action,
    invoiceKind,
    invoiceDisplayState,
    hasLinkedSource,
    reason,
    selectedSourceEffect: sourceEffect,
  });
  const partialCreditIsAllowed = canIssuePartialInvoiceCredit({
    allowPartialCredit,
    sourceEffect: resolvedSourceEffect,
  });
  const effectiveCreditMode = partialCreditIsAllowed ? creditMode : "FULL";
  const amountInCents =
    effectiveCreditMode === "FULL"
      ? creditableAmountInCents
      : currencyValueToCents(creditAmount);
  const remainingCreditableAmountInCents = Math.max(
    0,
    creditableAmountInCents - amountInCents,
  );
  const membershipGuidance = isMembershipInvoice && action !== "DELETE_DRAFT"
    ? getMembershipInvoiceCancellationGuidance(action)
    : null;
  const refusedForMembership = isInvoiceLifecycleChoiceRefusedForMembership({
    isMembershipInvoice,
    reason,
    sourceEffect,
  });
  const refundMethodOptionsKey = refundMethodOptions.join("\u0000");
  const normalizedRefundMethodOptions = useMemo(
    () =>
      Array.from(
        new Set(
          refundMethodOptions.map((option) => option.trim()).filter(Boolean),
        ),
      ),
    // Depend on the stable value content, not an inline array identity from a caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refundMethodOptionsKey],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setReason("");
    setReasonExplanation("");
    setSourceEffect("");
    setCreditMode("FULL");
    setCreditAmount(centsToCurrencyValue(creditableAmountInCents));
    setRefundMethodLabel(normalizedRefundMethodOptions[0] || "");
    setRefundDate(getChamberLocalDateInputValue({ timeZone }));
    setRefundReference("");
    setValidationError(null);
    setFieldErrors({});
    if (!submitInFlightRef.current) {
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [creditableAmountInCents, normalizedRefundMethodOptions, open, timeZone]);

  useEffect(() => {
    if (resolvedSourceEffect === "REVERSE_SOURCE") {
      setCreditMode("FULL");
    }
  }, [resolvedSourceEffect]);

  const getRequiredFieldErrors = () => {
    const errors: Partial<Record<InvoiceLifecycleRequiredField, true>> = {};
    if (action === "DELETE_DRAFT") {
      return errors;
    }
    if (!reason) {
      errors.reason = true;
    }
    if (reason === "OTHER" && !reasonExplanation.trim()) {
      errors.explanation = true;
    }
    if (reason === "OTHER" && hasLinkedSource && !resolvedSourceEffect) {
      errors.sourceEffect = true;
    }
    return errors;
  };

  const clearFieldError = (field: InvoiceLifecycleRequiredField) =>
    setFieldErrors((current) => {
      if (!current[field]) {
        return current;
      }
      const next = { ...current };
      delete next[field];
      return next;
    });

  const validate = () => {
    if (action === "DELETE_DRAFT") {
      return null;
    }

    if (refusedForMembership && membershipGuidance) {
      return membershipGuidance;
    }

    if (action === "ISSUE_CREDIT") {
      if (
        !Number.isSafeInteger(amountInCents) ||
        amountInCents <= 0 ||
        amountInCents > creditableAmountInCents
      ) {
        return `Enter a Credit Amount from $0.01 to ${formatCurrency(creditableAmountInCents)}.`;
      }

      if (
        resolvedSourceEffect === "REVERSE_SOURCE" &&
        amountInCents !== creditableAmountInCents
      ) {
        return `To cancel the ticket, membership, sponsorship, or ad too, credit the full Eligible Paid Amount of ${formatCurrency(creditableAmountInCents)}. Select Full Credit.`;
      }

      if (!refundMethodLabel.trim()) {
        // A select when the chamber has refund methods, else a typed field.
        return normalizedRefundMethodOptions.length
          ? selectFieldMessage("Refund Method")
          : enterFieldMessage("Refund Method");
      }

      if (!chamberDateInputToUtcMilli({ dateInput: refundDate, timeZone })) {
        return selectFieldMessage("Refund Date");
      }
    }

    return null;
  };

  const handleConfirm = async () => {
    if (effectiveLoading || submitInFlightRef.current) {
      return;
    }

    const nextFieldErrors = getRequiredFieldErrors();
    setFieldErrors(nextFieldErrors);
    if (Object.keys(nextFieldErrors).length > 0) {
      setValidationError(null);
      return;
    }

    const nextValidationError = validate();

    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    setValidationError(null);
    setSubmitError(null);
    submitInFlightRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm({
        action,
        ...(action === "DELETE_DRAFT"
          ? {}
          : {
              reason: reason || undefined,
              reasonExplanation:
                reason === "OTHER" ? reasonExplanation.trim() : undefined,
              sourceEffect: resolvedSourceEffect,
            }),
        ...(action === "ISSUE_CREDIT"
          ? {
              amountInCents,
              refundedAtUtcMilli: chamberDateInputToUtcMilli({
                dateInput: refundDate,
                timeZone,
              }),
              refundMethodLabel: refundMethodLabel.trim(),
              refundReference: refundReference.trim() || undefined,
            }
          : {}),
      });
    } catch (error) {
      setSubmitError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            action === "DELETE_DRAFT"
              ? "delete the draft invoice"
              : action === "VOID"
                ? "void the invoice"
                : "credit or refund the invoice",
          ),
        ),
      );
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const Icon =
    action === "DELETE_DRAFT"
      ? DeleteOutlineIcon
      : action === "VOID"
        ? RemoveCircleOutlineIcon
        : ReceiptLongOutlinedIcon;

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      onClose={effectiveLoading ? undefined : onClose}
      open={open}
      PaperProps={{
        sx: invoiceDialogPaperSx({
          maxWidth: action === "ISSUE_CREDIT" ? 680 : 440,
        }),
      }}
    >
      <DialogTitle
        component="div"
        className="relative px-6 pb-0 pt-7 text-center"
        sx={{ fontFamily: invoiceDesignFontFamily }}
      >
        <InvoiceDialogCloseButton
          ariaLabel={`Close ${copy.title}`}
          disabled={effectiveLoading}
          onClick={onClose}
        />
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#F21B1B] text-white">
          <Icon sx={{ fontSize: 36 }} />
        </div>
        <h2 className="mt-[18px] text-[18px] font-bold text-[#0A0A0A]">
          {copy.title}
        </h2>
      </DialogTitle>

      <DialogContent
        className="px-7 pb-2 pt-3"
        sx={{ fontFamily: invoiceDesignFontFamily }}
      >
        <p className="text-center text-sm leading-6 text-[#0A0A0A]">
          {creditsAndVoidsEventRegistration
            ? "Credit the full recorded payment amount and void the unpaid balance. The original invoice and payment history will remain in the transaction history. All tickets in this registration will be cancelled and released for sale. The registration will be removed from active attendance."
            : cancelsEventRegistration
              ? "Void this invoice and keep it in the transaction history. All tickets in this registration will be cancelled and released for sale. The registration will be removed from active attendance."
            : copy.description}
        </p>
        <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3 text-sm">
          <div className="font-bold text-gray-950">{invoiceNumber}</div>
          <div className="mt-1 text-gray-700">{customerName}</div>
        </div>

        {action !== "DELETE_DRAFT" ? (
          <div className="mt-5 space-y-4">
            <div>
              <label
                className={lifecycleFieldLabelClassName}
                id="invoice-lifecycle-reason-label"
              >
                {action === "VOID" ? "Reason for Voiding" : "Reason for Credit"}{" "}
                <span className="text-[#F0202E]">*</span>
              </label>
              <InvoiceSearchableSelect
                error={Boolean(fieldErrors.reason)}
                getOptionDisabled={(option) =>
                  isInvoiceLifecycleChoiceRefusedForMembership({
                    isMembershipInvoice,
                    reason: option.value,
                  })
                }
                getOptionKey={(option) => option.value}
                getOptionLabel={(option) => option.label}
                helperText={fieldErrors.reason ? requiredFieldMessage : undefined}
                id="invoice-lifecycle-reason"
                labelledById="invoice-lifecycle-reason-label"
                onChange={(option) => {
                  setReason(option?.value || "");
                  setSourceEffect("");
                  clearFieldError("reason");
                  setValidationError(null);
                  setSubmitError(null);
                }}
                options={reasonOptions}
                placeholder="Select Reason"
                required
                searchPlaceholder="Search Reason"
                value={
                  reasonOptions.find((option) => option.value === reason) ||
                  null
                }
              />
            </div>

            {reason === "OTHER" ? (
              <div>
                <label
                  className={lifecycleFieldLabelClassName}
                  htmlFor="invoice-lifecycle-explanation"
                >
                  Explanation <span className="text-[#F0202E]">*</span>
                </label>
                <TextField
                  error={Boolean(fieldErrors.explanation)}
                  fullWidth
                  helperText={
                    fieldErrors.explanation ? requiredFieldMessage : undefined
                  }
                  id="invoice-lifecycle-explanation"
                  minRows={3}
                  multiline
                  onChange={({ target }) => {
                    setReasonExplanation(target.value);
                    clearFieldError("explanation");
                    setValidationError(null);
                    setSubmitError(null);
                  }}
                  placeholder={
                    action === "VOID"
                      ? "Explain why this invoice is being voided"
                      : undefined
                  }
                  required
                  sx={invoiceFieldSx}
                  value={reasonExplanation}
                />
              </div>
            ) : null}

            {reason === "OTHER" && hasLinkedSource && !releasesEventSource ? (
              <div>
                <label
                  className={lifecycleFieldLabelClassName}
                  id="invoice-lifecycle-source-effect-label"
                >
                  Linked Source Action <span className="text-[#F0202E]">*</span>
                </label>
                <InvoiceSearchableSelect
                  error={Boolean(fieldErrors.sourceEffect)}
                  getOptionDisabled={(option) =>
                    isMembershipInvoice && option.value === "REVERSE_SOURCE"
                  }
                  getOptionKey={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                  helperText={
                    fieldErrors.sourceEffect ? requiredFieldMessage : undefined
                  }
                  id="invoice-lifecycle-source-effect"
                  labelledById="invoice-lifecycle-source-effect-label"
                  onChange={(option) => {
                    setSourceEffect(option?.value || "");
                    clearFieldError("sourceEffect");
                    setValidationError(null);
                    setSubmitError(null);
                  }}
                  options={sourceEffectOptions}
                  placeholder="Select Linked Source Action"
                  required
                  searchPlaceholder="Search"
                  value={
                    sourceEffectOptions.find(
                      (option) => option.value === sourceEffect,
                    ) || null
                  }
                />
              </div>
            ) : null}

            {membershipGuidance ? (
              <Alert severity="info">{membershipGuidance}</Alert>
            ) : null}
          </div>
        ) : null}

        {action === "ISSUE_CREDIT" ? (
          <div className="mt-5 space-y-4 border-t border-gray-200 pt-5">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-gray-700">
                Eligible Paid Amount
              </div>
              <div className="mt-1 text-2xl font-bold text-gray-950">
                {formatCurrency(creditableAmountInCents)}
              </div>
            </div>

            {allowPartialCredit ? (
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Full Credit", value: "FULL" as const },
                  { label: "Partial Credit", value: "PARTIAL" as const },
                ].map((option) => (
                  <button
                    aria-pressed={effectiveCreditMode === option.value}
                    className={`min-h-11 rounded-lg border px-3 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                      effectiveCreditMode === option.value
                        ? "border-[#0091FF] bg-blue-50 text-[#006FC9]"
                        : "border-gray-300 bg-white text-gray-800"
                    }`}
                    disabled={
                      option.value === "PARTIAL" && !partialCreditIsAllowed
                    }
                    key={option.value}
                    onClick={() => {
                      setCreditMode(option.value);
                      setValidationError(null);
                    }}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : (
              <Alert severity="info">
                This invoice is only partly paid. Issue Credit will credit the
                full amount paid and void the unpaid balance in one step.
              </Alert>
            )}

            {allowPartialCredit && resolvedSourceEffect === "REVERSE_SOURCE" ? (
              <Alert severity="info">
                To cancel the ticket, membership, sponsorship, or ad too, you
                must credit the full Eligible Paid Amount. Partial Credit is not
                available for this reason.
              </Alert>
            ) : null}

            {partialCreditIsAllowed && creditMode === "PARTIAL" ? (
              <div>
                <div className="mb-1 text-xs font-bold uppercase text-gray-700">
                  Credit Amount
                </div>
                <CurrencyInput
                  adornmentClassName="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-gray-500"
                  ariaLabel="Credit amount"
                  inputClassName={amountInputClassName}
                  onChange={(value) => {
                    setCreditAmount(value);
                    setValidationError(null);
                  }}
                  value={creditAmount}
                />
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm">
              <div>
                <div className="font-semibold text-gray-600">Credit</div>
                <div className="mt-1 font-bold text-gray-950">
                  {formatCurrency(amountInCents)}
                </div>
              </div>
              <div>
                <div className="font-semibold text-gray-600">
                  Remaining Eligible
                </div>
                <div className="mt-1 font-bold text-gray-950">
                  {formatCurrency(remainingCreditableAmountInCents)}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="font-bold text-gray-950">Refund Customer</div>
              <p className="mt-1 text-sm leading-5 text-gray-600">
                This does not send money back. Refund the payment in Stripe or
                in the {noun.singular} accounting system first, then record it
                here.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {normalizedRefundMethodOptions.length ? (
                  <TextField
                    fullWidth
                    label="Refund Method"
                    onChange={({ target }) => {
                      setRefundMethodLabel(target.value);
                      setValidationError(null);
                    }}
                    required
                    select
                    sx={invoiceFieldSx}
                    value={refundMethodLabel}
                  >
                    {normalizedRefundMethodOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    fullWidth
                    label="Refund Method"
                    onChange={({ target }) => {
                      setRefundMethodLabel(target.value);
                      setValidationError(null);
                    }}
                    placeholder="For example, accounting software refund"
                    required
                    sx={invoiceFieldSx}
                    value={refundMethodLabel}
                  />
                )}
                <div>
                  <label
                    htmlFor="invoice-refund-date"
                    className="mb-1 block text-sm font-bold text-gray-900"
                  >
                    Refund Date <span className="text-red-500">*</span>
                  </label>
                  <CalendarDateInput
                    id="invoice-refund-date"
                    ariaLabel="Refund Date"
                    mode="fullDate"
                    iconPosition="left"
                    value={refundDate}
                    onChange={(value) => {
                      setRefundDate(value);
                      setValidationError(null);
                    }}
                  />
                </div>
              </div>
              <TextField
                className="mt-4"
                fullWidth
                label="External Reference"
                onChange={({ target }) => setRefundReference(target.value)}
                placeholder="Optional accounting reference"
                sx={invoiceFieldSx}
                value={refundReference}
              />
            </div>
          </div>
        ) : null}

        {validationError || submitError || actionError ? (
          <Alert className="mt-4" severity="error">
            {validationError || submitError || actionError}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions
        className="gap-3 bg-[#F4F8FF] px-7 py-4"
        sx={{ fontFamily: invoiceDesignFontFamily }}
      >
        <Button
          disabled={effectiveLoading || refusedForMembership}
          fullWidth
          onClick={() => void handleConfirm()}
          startIcon={
            effectiveLoading ? (
              <CircularProgress color="inherit" size={17} />
            ) : (
              <Icon />
            )
          }
          sx={{
            ...invoiceModalActionSx({
              backgroundColor:
                action === "ISSUE_CREDIT"
                  ? invoiceDesignTokens.blue
                  : invoiceDesignTokens.closeRed,
              hoverBackgroundColor:
                action === "ISSUE_CREDIT" ? "#0077D4" : "#D90F1A",
            }),
          }}
          variant="contained"
        >
          {copy.confirmLabel}
        </Button>
        <Button
          disabled={effectiveLoading}
          fullWidth
          onClick={onClose}
          sx={{
            ...invoiceModalActionSx({
              backgroundColor: invoiceDesignTokens.dark,
              hoverBackgroundColor: "#111827",
            }),
          }}
          variant="contained"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceLifecycleActionDialog;
